/**
 * Catálogos académicos: ciclos, grados y secciones (solo Administración).
 *
 * La pantalla muestra siempre **cuánto se usa** cada cosa —cargos de un ciclo,
 * estudiantes de un grado o sección— antes de ofrecer eliminarla. Eliminar un grado
 * con expediente no es un descuido posible: el servidor lo rechaza, pero la
 * interfaz debe explicarlo antes de que alguien lo intente.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiRequestError, apiRequest } from '../api/client.js';
import type { StoredSession } from '../local/session.js';

interface Ciclo {
  id: string;
  name: string;
  year: number;
  startDate: string;
  endDate: string;
  closed: boolean;
  isCurrent: boolean;
  version: number;
  chargeCount: number;
}

interface Seccion {
  id: string;
  name: string;
  /** Control de concurrencia optimista al renombrar. */
  version: number;
  studentCount: number;
}

interface Grado {
  id: string;
  name: string;
  version: number;
  studentCount: number;
  sections: Seccion[];
}

export function AcademicCatalogPage(props: {
  session: StoredSession;
  conexion: string;
}): JSX.Element {
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [grados, setGrados] = useState<Grado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  // Alta de ciclo
  const [nombreCiclo, setNombreCiclo] = useState('');
  const [anio, setAnio] = useState(String(new Date().getFullYear() + 1));
  const [inicio, setInicio] = useState('');
  const [fin, setFin] = useState('');

  // Alta de grado y de sección
  const [nombreGrado, setNombreGrado] = useState('');
  const [nombreSeccion, setNombreSeccion] = useState<Record<string, string>>({});

  /**
   * Renombrado en línea.
   *
   * Solo uno a la vez, y por eso los tres estados están separados: si el nombre en
   * edición fuera un mapa por identificador, dos filas abiertas a la vez podrían
   * guardarse con el texto de la otra.
   */
  const [renombrandoGrado, setRenombrandoGrado] = useState<string | null>(null);
  const [renombrandoSeccion, setRenombrandoSeccion] = useState<string | null>(null);
  const [nombreNuevo, setNombreNuevo] = useState('');

  const empezarRenombrado = (que: 'grado' | 'seccion', id: string, nombre: string): void => {
    setRenombrandoGrado(que === 'grado' ? id : null);
    setRenombrandoSeccion(que === 'seccion' ? id : null);
    setNombreNuevo(nombre);
    setError(null);
  };

  const cancelarRenombrado = (): void => {
    setRenombrandoGrado(null);
    setRenombrandoSeccion(null);
    setNombreNuevo('');
  };

  const renombrarGrado = async (grado: Grado): Promise<void> => {
    setError(null);
    try {
      await apiRequest(`/v1/grades/${grado.id}`, {
        method: 'PATCH',
        body: { name: nombreNuevo.trim(), expectedVersion: grado.version },
      });
      setAviso(`El grado "${grado.name}" ahora se llama "${nombreNuevo.trim()}".`);
      cancelarRenombrado();
      await cargar();
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError ? problema.message : 'No fue posible renombrar el grado.',
      );
    }
  };

  const renombrarSeccion = async (grado: Grado, seccion: Seccion): Promise<void> => {
    setError(null);
    try {
      await apiRequest(`/v1/sections/${seccion.id}`, {
        method: 'PATCH',
        body: { name: nombreNuevo.trim(), expectedVersion: seccion.version },
      });
      setAviso(
        `La sección "${seccion.name}" de ${grado.name} ahora se llama "${nombreNuevo.trim()}".`,
      );
      cancelarRenombrado();
      await cargar();
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError
          ? problema.message
          : 'No fue posible renombrar la sección.',
      );
    }
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [datosCiclos, datosGrados] = await Promise.all([
        apiRequest<{ cycles: Ciclo[] }>('/v1/cycles'),
        apiRequest<{ grades: Grado[] }>('/v1/grades'),
      ]);
      setCiclos(datosCiclos.cycles);
      setGrados(datosGrados.grades);
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? 'Los catálogos académicos necesitan conexión con el servidor.'
          : 'No fue posible cargar los catálogos.',
      );
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const mensaje = (problema: unknown, respaldo: string): string =>
    problema instanceof ApiRequestError ? problema.message : respaldo;

  const crearCiclo = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    setError(null);
    try {
      await apiRequest('/v1/cycles', {
        method: 'POST',
        body: { name: nombreCiclo, year: Number(anio), startDate: inicio, endDate: fin },
      });
      setAviso(
        `Ciclo "${nombreCiclo}" creado. Nace abierto y NO vigente: márquelo como vigente ` +
          'cuando quiera que sea el ciclo por defecto para cobrar.',
      );
      setNombreCiclo('');
      setInicio('');
      setFin('');
      await cargar();
    } catch (problema) {
      setError(mensaje(problema, 'No fue posible crear el ciclo.'));
    }
  };

  const marcarVigente = async (ciclo: Ciclo): Promise<void> => {
    const confirmado = window.confirm(
      `¿Marcar "${ciclo.name}" como el ciclo vigente?\n\n` +
        'Será el ciclo por defecto al generar cargos y al cobrar. El que lo sea ahora ' +
        'dejará de serlo.',
    );
    if (!confirmado) return;
    setError(null);
    try {
      await apiRequest(`/v1/cycles/${ciclo.id}`, {
        method: 'PATCH',
        body: { isCurrent: true, expectedVersion: ciclo.version },
      });
      setAviso(`"${ciclo.name}" es ahora el ciclo vigente.`);
      await cargar();
    } catch (problema) {
      setError(mensaje(problema, 'No fue posible cambiar el ciclo vigente.'));
    }
  };

  const alternarCierre = async (ciclo: Ciclo): Promise<void> => {
    const cerrar = !ciclo.closed;
    if (cerrar) {
      const confirmado = window.confirm(
        `¿Cerrar "${ciclo.name}"?\n\n` +
          'No se podrán generar cargos ni registrar pagos nuevos en este ciclo. ' +
          'Los cargos y pagos existentes se conservan intactos, y podrá reabrirlo.',
      );
      if (!confirmado) return;
    }
    setError(null);
    try {
      await apiRequest(`/v1/cycles/${ciclo.id}`, {
        method: 'PATCH',
        body: { closed: cerrar, expectedVersion: ciclo.version },
      });
      setAviso(cerrar ? `"${ciclo.name}" fue cerrado.` : `"${ciclo.name}" fue reabierto.`);
      await cargar();
    } catch (problema) {
      setError(mensaje(problema, 'No fue posible cambiar el estado del ciclo.'));
    }
  };

  const crearGrado = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    setError(null);
    try {
      await apiRequest('/v1/grades', {
        method: 'POST',
        // Se coloca al final del orden actual; reordenar es otra decisión.
        body: { name: nombreGrado, orderIndex: grados.length },
      });
      setAviso(`Grado "${nombreGrado}" creado. Agréguele al menos una sección.`);
      setNombreGrado('');
      await cargar();
    } catch (problema) {
      setError(mensaje(problema, 'No fue posible crear el grado.'));
    }
  };

  const crearSeccion = async (grado: Grado): Promise<void> => {
    const nombre = (nombreSeccion[grado.id] ?? '').trim();
    if (nombre === '') return;
    setError(null);
    try {
      await apiRequest('/v1/sections', {
        method: 'POST',
        body: { gradeId: grado.id, name: nombre },
      });
      setAviso(`Sección "${nombre}" agregada a ${grado.name}.`);
      setNombreSeccion({ ...nombreSeccion, [grado.id]: '' });
      await cargar();
    } catch (problema) {
      setError(mensaje(problema, 'No fue posible crear la sección.'));
    }
  };

  const eliminarGrado = async (grado: Grado): Promise<void> => {
    const confirmado = window.confirm(`¿Eliminar el grado "${grado.name}"?`);
    if (!confirmado) return;
    setError(null);
    try {
      await apiRequest(`/v1/grades/${grado.id}`, { method: 'DELETE' });
      setAviso(`Grado "${grado.name}" eliminado.`);
      await cargar();
    } catch (problema) {
      setError(mensaje(problema, 'No fue posible eliminar el grado.'));
    }
  };

  const eliminarSeccion = async (grado: Grado, seccion: Seccion): Promise<void> => {
    const confirmado = window.confirm(
      `¿Eliminar la sección "${seccion.name}" de ${grado.name}?`,
    );
    if (!confirmado) return;
    setError(null);
    try {
      await apiRequest(`/v1/sections/${seccion.id}`, { method: 'DELETE' });
      setAviso(`Sección "${seccion.name}" eliminada.`);
      await cargar();
    } catch (problema) {
      setError(mensaje(problema, 'No fue posible eliminar la sección.'));
    }
  };

  if (props.conexion !== 'EN_LINEA') {
    return (
      <section aria-labelledby="titulo-catalogos">
        <h1 id="titulo-catalogos">Ciclos, grados y secciones</h1>
        <div className="aviso-caja" role="status">
          Esta sección necesita conexión con el servidor.
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="titulo-catalogos">
      <h1 id="titulo-catalogos">Ciclos, grados y secciones</h1>
      <p className="ayuda">
        Nada con historial se elimina: un grado con estudiantes asignados o un ciclo con
        cargos son parte del expediente académico. Para retirarlos de uso, ciérrelos.
      </p>

      {aviso !== null && (
        <div className="aviso-caja ok" role="status">
          {aviso}
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}
      {cargando && <p role="status">Cargando catálogos…</p>}

      {/* --- Ciclos ------------------------------------------------------- */}
      <h2>Ciclos escolares</h2>

      <div className="tabla-desplazable">
        <table className="tabla">
          <caption className="oculto-visual">Ciclos escolares de la institución</caption>
          <thead>
            <tr>
              <th scope="col">Ciclo</th>
              <th scope="col">Año</th>
              <th scope="col">Inicio</th>
              <th scope="col">Fin</th>
              <th scope="col" className="numero">Cargos</th>
              <th scope="col">Estado</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {ciclos.map((ciclo) => (
              <tr key={ciclo.id}>
                <th scope="row">{ciclo.name}</th>
                <td>{ciclo.year}</td>
                <td>{ciclo.startDate}</td>
                <td>{ciclo.endDate}</td>
                <td className="numero">{ciclo.chargeCount}</td>
                <td>
                  {ciclo.isCurrent && <span className="marca-estado ok">★ Vigente</span>}
                  {ciclo.closed ? (
                    <span className="marca-estado error"> ✕ Cerrado</span>
                  ) : (
                    <span className="marca-estado"> ○ Abierto</span>
                  )}
                </td>
                <td className="acciones">
                  {!ciclo.isCurrent && !ciclo.closed && (
                    <button type="button" onClick={() => void marcarVigente(ciclo)}>
                      Marcar vigente
                    </button>
                  )}
                  <button type="button" onClick={() => void alternarCierre(ciclo)}>
                    {ciclo.closed ? 'Reabrir' : 'Cerrar'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form className="tarjeta" onSubmit={(evento) => void crearCiclo(evento)}>
        <h3>Nuevo ciclo</h3>
        <div className="campos">
          <label>
            Nombre
            <input
              type="text"
              value={nombreCiclo}
              onChange={(e) => setNombreCiclo(e.target.value)}
              required
              minLength={3}
              placeholder="Ciclo 2028"
            />
          </label>
          <label>
            Año
            <input
              type="number"
              value={anio}
              onChange={(e) => setAnio(e.target.value)}
              required
              min={2000}
              max={2999}
            />
          </label>
          <label>
            Inicio
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} required />
          </label>
          <label>
            Fin
            <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} required />
          </label>
        </div>
        <p className="ayuda">
          El ciclo nace abierto y <strong>no vigente</strong>. Puede generarle cargos y cobrarlo
          por anticipado aunque los estudiantes todavía no tengan grado asignado.
        </p>
        <button type="submit" className="boton">
          Crear ciclo
        </button>
      </form>

      {/* --- Grados y secciones -------------------------------------------- */}
      <h2>Grados y secciones</h2>
      <p className="ayuda">
        Cada grado agrupa sus secciones. Junto a cada una se ve cuántos estudiantes
        tiene: <strong>solo se puede eliminar lo que no tenga expediente</strong>, y por
        eso el botón aparece o no según el caso, en vez de fallar al pulsarlo.
      </p>

      <div className="tarjeta tabla-desplazable">
        <table>
          <caption className="oculto-visual">
            Grados de la institución con sus secciones y cuántos estudiantes tiene cada una
          </caption>
          <thead>
            <tr>
              <th scope="col">Grado</th>
              <th scope="col" className="numero">Estudiantes</th>
              <th scope="col">Secciones</th>
              <th scope="col">Acciones del grado</th>
            </tr>
          </thead>
          <tbody>
            {grados.map((grado) => (
              <tr key={grado.id}>
                <th scope="row">
                  {renombrandoGrado === grado.id ? (
                    <div className="edicion-en-linea">
                      <label>
                        <span className="oculto-visual">Nuevo nombre de {grado.name}</span>
                        <input
                          value={nombreNuevo}
                          onChange={(e) => setNombreNuevo(e.target.value)}
                          aria-label={`Nuevo nombre de ${grado.name}`}
                          autoFocus
                        />
                      </label>
                      <button
                        type="button"
                        className="boton compacto primario"
                        onClick={() => void renombrarGrado(grado)}
                        disabled={nombreNuevo.trim() === ''}
                      >
                        Guardar
                      </button>
                      <button type="button" className="boton compacto" onClick={cancelarRenombrado}>
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    grado.name
                  )}
                </th>

                <td className="numero">{grado.studentCount}</td>

                <td>
                  {grado.sections.length === 0 ? (
                    <span className="ayuda">
                      Sin secciones. Sin al menos una no se le puede asignar estudiantes.
                    </span>
                  ) : (
                    <ul className="fichas-seccion">
                      {grado.sections.map((seccion) => (
                        <li key={seccion.id}>
                          {renombrandoSeccion === seccion.id ? (
                            <div className="edicion-en-linea">
                              <label>
                                <span className="oculto-visual">Nuevo nombre de {seccion.name}</span>
                                <input
                                  value={nombreNuevo}
                                  onChange={(e) => setNombreNuevo(e.target.value)}
                                  aria-label={`Nuevo nombre de la sección ${seccion.name}`}
                                  size={6}
                                  autoFocus
                                />
                              </label>
                              <button
                                type="button"
                                className="boton compacto primario"
                                onClick={() => void renombrarSeccion(grado, seccion)}
                                disabled={nombreNuevo.trim() === ''}
                              >
                                Guardar
                              </button>
                              <button
                                type="button"
                                className="boton compacto"
                                onClick={cancelarRenombrado}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <span className="ficha-seccion">
                              <strong>{seccion.name}</strong>
                              <span className="ayuda">{seccion.studentCount} est.</span>
                              <button
                                type="button"
                                className="boton compacto"
                                onClick={() => empezarRenombrado('seccion', seccion.id, seccion.name)}
                              >
                                Editar
                              </button>
                              {seccion.studentCount === 0 ? (
                                <button
                                  type="button"
                                  className="boton compacto peligro"
                                  onClick={() => void eliminarSeccion(grado, seccion)}
                                >
                                  Eliminar
                                </button>
                              ) : (
                                <span className="ayuda" title="Tiene expediente académico">
                                  con expediente
                                </span>
                              )}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Alta de sección, en la misma fila del grado al que pertenece. */}
                  <div className="agregar-seccion">
                    <label>
                      <span className="oculto-visual">Nueva sección de {grado.name}</span>
                      <input
                        type="text"
                        value={nombreSeccion[grado.id] ?? ''}
                        onChange={(e) =>
                          setNombreSeccion({ ...nombreSeccion, [grado.id]: e.target.value })
                        }
                        placeholder="A, B…"
                        maxLength={40}
                        size={8}
                        aria-label={`Nueva sección de ${grado.name}`}
                      />
                    </label>
                    <button
                      type="button"
                      className="boton compacto"
                      onClick={() => void crearSeccion(grado)}
                      disabled={(nombreSeccion[grado.id] ?? '').trim() === ''}
                    >
                      + Agregar sección
                    </button>
                  </div>
                </td>

                <td>
                  <div className="pila">
                    <button
                      type="button"
                      className="boton compacto"
                      onClick={() => empezarRenombrado('grado', grado.id, grado.name)}
                    >
                      Editar
                    </button>
                    {grado.sections.length === 0 && grado.studentCount === 0 ? (
                      <button
                        type="button"
                        className="boton compacto peligro"
                        onClick={() => void eliminarGrado(grado)}
                      >
                        Eliminar
                      </button>
                    ) : (
                      <span className="ayuda">
                        {grado.studentCount > 0 ? 'Tiene estudiantes' : 'Tiene secciones'}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {grados.length === 0 && !cargando && (
              <tr>
                <td colSpan={4} className="vacio">
                  Todavía no hay grados. Cree el primero abajo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form className="tarjeta" onSubmit={(evento) => void crearGrado(evento)}>
        <h3>Nuevo grado</h3>
        <label>
          Nombre del grado
          <input
            type="text"
            value={nombreGrado}
            onChange={(e) => setNombreGrado(e.target.value)}
            required
            minLength={2}
            placeholder="Cuarto Primaria"
          />
        </label>
        <button type="submit" className="boton primario">
          Crear grado
        </button>
      </form>
    </section>
  );
}
